import { CheckCircle2 } from "lucide-react";
import { mediaUrl, youTubeId } from "@/lib/api";
import { Btn, Disclaimer, SectionHead } from "@/components/public/Ui";

function Paragraphs({ text }) {
  return (
    <div className="bka-prose">
      {String(text || "")
        .split("\n\n")
        .map((p, i) => (
          <p key={i} dangerouslySetInnerHTML={{ __html: inlineMarkup(p) }} />
        ))}
    </div>
  );
}

function inlineMarkup(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\n/g, "<br/>");
}

export function ContentSections({ sections = [] }) {
  return (
    <div className="space-y-14 lg:space-y-20">
      {sections.map((section, idx) => (
        <Section key={idx} section={section} index={idx} />
      ))}
    </div>
  );
}

function Section({ section, index }) {
  const testId = `content-section-${index}`;

  if (section.type === "disclaimer") {
    return <Disclaimer title={section.title} body={section.body} testId={testId} />;
  }


  if (section.type === "media") {
    const url = mediaUrl(section.media_url);
    const youtube = section.media_type === "video" ? youTubeId(section.media_url || "") : null;
    if (!section.media_url) return null;
    return (
      <section data-testid={testId}>
        {section.title && <SectionHead title={section.title} />}
        <figure className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_18px_48px_-34px_rgba(11,53,84,0.5)]">
          {section.media_type === "video" ? (
            youtube ? (
              <div className="aspect-video">
                <iframe
                  className="h-full w-full"
                  src={`https://www.youtube.com/embed/${youtube}`}
                  title={section.title || section.caption || "Video"}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <video
                src={url}
                controls
                preload="metadata"
                className="max-h-[70vh] w-full bg-black"
              >
                Your browser does not support embedded video.
              </video>
            )
          ) : (
            <img
              src={url}
              alt={section.alt_text || section.title || ""}
              className="max-h-[70vh] w-full object-cover"
              loading="lazy"
            />
          )}
          {section.caption && (
            <figcaption className="px-5 py-4 text-sm leading-6 text-bka-ink/65">
              {section.caption}
            </figcaption>
          )}
        </figure>
      </section>
    );
  }

  if (section.type === "team") {
    return (
      <section data-testid={testId}>
        <SectionHead title={section.title || "Board of Directors"} text={section.text} />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {(section.members || []).map((member, i) => (
            <article
              key={`${member.name || "member"}-${i}`}
              className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_18px_48px_-34px_rgba(11,53,84,0.5)]"
              data-testid={`${testId}-member-${i}`}
            >
              <div className="aspect-[4/5] overflow-hidden bg-slate-100">
                {member.image_url ? (
                  <img
                    src={mediaUrl(member.image_url)}
                    alt={member.name || "Board member"}
                    className="h-full w-full object-cover transition-transform duration-500 hover:scale-[1.02]"
                    style={{ objectPosition: member.image_position || "center top" }}
                    loading="lazy"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-sm font-semibold text-slate-400">
                    Photo coming soon
                  </div>
                )}
              </div>
              <div className="p-5">
                <p className="bka-overline text-bka-emerald">{member.role}</p>
                <h3 className="mt-2 font-display text-xl font-bold text-bka-navy">{member.name}</h3>
                {member.bio && <p className="mt-3 text-sm leading-relaxed text-bka-ink/75">{member.bio}</p>}
              </div>
            </article>
          ))}
        </div>
      </section>
    );
  }

  if (section.type === "bullets") {
    return (
      <section data-testid={testId}>
        <SectionHead title={section.title} />
        <div className="grid gap-4 sm:grid-cols-2">
          {(section.items || []).map((item, i) => (
            <div
              key={i}
              className="bka-card-lift bka-accent-edge overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 pl-7"
              data-testid={`${testId}-item-${i}`}
            >
              <CheckCircle2 className="mb-3 h-5 w-5 text-bka-orangeDark" />
              <h3 className="font-display text-lg font-extrabold uppercase tracking-[0.04em] text-bka-emeraldDark">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-bka-ink/80">{item.text}</p>
            </div>
          ))}
        </div>
        {section.footnote && <p className="mt-5 text-sm italic text-bka-ink/65">{section.footnote}</p>}
      </section>
    );
  }

  if (section.type === "cta") {
    return (
      <section data-testid={testId} className="relative overflow-hidden rounded-3xl bg-bka-navy p-8 lg:p-14">
        <div className="bka-grain absolute inset-0" aria-hidden="true" />
        <div className="relative max-w-2xl">
          <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">{section.title}</h2>
          {section.text && <p className="mt-4 text-base text-white/75">{section.text}</p>}
          <div className="mt-8 flex flex-wrap gap-3">
            {section.primary && section.primary.label && (
              <Btn to={section.primary.href} variant="primary" testId={`${testId}-primary-cta`}>
                {section.primary.label}
              </Btn>
            )}
            {section.secondary && section.secondary.label && (
              <Btn to={section.secondary.href} variant="light" testId={`${testId}-secondary-cta`}>
                {section.secondary.label}
              </Btn>
            )}
          </div>
        </div>
      </section>
    );
  }

  if (section.type === "gallery") {
    const images = (section.images || []).filter((i) => i.image_url);
    if (!images.length) return null;
    return (
      <section data-testid={testId}>
        {section.title && <SectionHead title={section.title} />}
        <div className="columns-1 gap-5 sm:columns-2 lg:columns-3 [&>*]:mb-5">
          {images.map((img, i) => (
            <figure key={i} className="overflow-hidden rounded-3xl border border-bka-emerald/15">
              <img
                src={mediaUrl(img.image_url)}
                alt={img.caption || ""}
                loading="lazy"
                className="w-full object-cover"
              />
              {img.caption && (
                <figcaption className="bg-white px-5 py-4 text-sm text-bka-ink/80">
                  {img.caption}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      </section>
    );
  }

  if (section.type === "honourees") {
    const people = (section.honourees || []).filter((h) => h.name);
    if (!people.length) return null;
    return (
      <section data-testid={testId}>
        {section.title && <SectionHead title={section.title} />}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {people.map((h, i) => (
            <article
              key={i}
              className="overflow-hidden rounded-3xl border border-bka-emerald/15 bg-white"
            >
              <div className="aspect-[4/5] bg-bka-aqua">
                {h.image_url && (
                  <img
                    src={mediaUrl(h.image_url)}
                    alt={h.name}
                    loading="lazy"
                    className="h-full w-full object-cover object-top"
                  />
                )}
              </div>
              <div className="p-5">
                {h.year && <p className="bka-overline text-bka-emerald">{h.year}</p>}
                <h3 className="mt-1.5 font-display text-base font-bold text-bka-navy">{h.name}</h3>
                {h.award && (
                  <p className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-bka-orangeDark">
                    {h.award}
                  </p>
                )}
                {h.statement && (
                  <p className="mt-3 text-sm leading-[1.75] text-bka-ink/80">{h.statement}</p>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section data-testid={testId}>
      {section.title && <SectionHead title={section.title} />}
      <Paragraphs text={section.body} />
      {section.placeholder && (
        <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-bka-aqua px-4 py-1.5 text-xs font-bold text-bka-teal">
          Awaiting client content — editable in the CMS
        </p>
      )}
    </section>
  );
}
